import React, { useState } from "react";
import Styles from "./RFPPlaceBid.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";

import TextInput from "../../../commons/Inputs/TextInput/TextInput";
import {
  createBid,
  updateBid,
} from "../../../Services/ReverseAuction/AlterApi";
import {
  formatDateTimeViewRfq,
  formatDateType,
} from "../../../commons/FormatDate";
import Countdown from "../../../commons/CountDownTimer/Countdown";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import Fileupload from "../../../commons/Inputs/FileUpload/Fileupload";
import { useLocation } from "react-router-dom";
import { decryptData } from "../../../commons/Decode";

const RFPPlaceBid = ({
  open,
  handleClose,
  tenderData,
  requisitionData,
  bidStage,
  handleUpdate,
  status,
  allRequisitions,
  checkType,
}) => {
  const location = useLocation();
  const isTradeConnect = location?.pathname === "/tradeconnect";
  const showSellerMinQty = !isTradeConnect;

  // ── Security mode derivation from checkType ──────────────────────────────
  // checkType holds all requisitions for the tender; filter to those with a placed bid
  const bidsPlaced = checkType?.filter((ele) => ele?.selfBid?.id) ?? [];
  const firstBid = bidsPlaced?.[0]?.selfBid;

  // If ANY bid exists, the mode is locked to whatever that bid used
  const isBgLocked =
    !!requisitionData?.selfBid?.documentId || !!firstBid?.documentId;
  const isEmdLocked = !!firstBid && !firstBid?.documentId;

  // Cross-requisition mandatory checks (only when NOT already locked)
  // BG is only forced mandatory on TradeConnect
  const isBgMandatory =
    isTradeConnect &&
    !isBgLocked &&
    !isEmdLocked &&
    (allRequisitions ?? [])
      .filter((r) => r.id !== requisitionData?.id)
      .some((r) => !!r?.selfBid?.documentId);

  const isEmdMandatory =
    !isBgLocked &&
    !isEmdLocked &&
    !isBgMandatory &&
    (allRequisitions ?? [])
      .filter((r) => r.id !== requisitionData?.id)
      .some((r) => !!r?.selfBid && !r?.selfBid?.documentId);

  // Switching is only allowed when no bids have been placed yet
  const canSwitch = bidsPlaced.length === 0;

  // ── Initial security mode: BG by default (index 0); EMD only if forced ───
  const initialMode = isEmdLocked || isEmdMandatory ? "emd" : "bg";

  // ── Redux ─────────────────────────────────────────────────────────────────
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const parentEntityCompanyId = useSelector(
    (state) => state?.persistedReducer?.user?.parentEntityCompanyId,
  );

  // ── State ─────────────────────────────────────────────────────────────────
  const [securityMode, setSecurityMode] = useState(initialMode);
  const [bankGuaranteeAmount, setBankGuaranteeAmount] = useState(
    requisitionData?.selfBid?.bankGuaranteeAmount ||
      firstBid?.bankGuaranteeAmount ||
      "",
  );
  const [bidPrice, setBidPrice] = useState(
    requisitionData?.selfBid?.bidPrice || "",
  );
  const [bidQuantity, setBidQuantity] = useState(
    requisitionData?.selfBid?.bidQuantityMw || "",
  );
  const [minimumQuantity, setMinimumQuantity] = useState(
    requisitionData?.selfBid?.minBidQuantityMw ?? "",
  );
  const [docId, setDocId] = useState(
    requisitionData?.selfBid?.documentId || firstBid?.documentId || null,
  );
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    data: "",
    note: "",
  });
  const [securityModeConfirm, setSecurityModeConfirm] = useState({
    open: false,
    target: null,
  });
  const [bgDocConfirm, setBgDocConfirm] = useState({
    open: false,
    pendingVal: null,
  });
  const [bgUploadKey, setBgUploadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const isUpdate = !!requisitionData?.selfBid;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSecurityModeClick = (target) => {
    if (target === securityMode) return;
    if (
      !canSwitch ||
      isBgLocked ||
      isEmdLocked ||
      isBgMandatory ||
      isEmdMandatory
    )
      return;
    setSecurityModeConfirm({ open: true, target });
  };

  const confirmSecurityModeSwitch = () => {
    setSecurityMode(securityModeConfirm.target);
    setSecurityModeConfirm({ open: false, target: null });
  };

  const handleBidPrice = (v) => {
    if (v < 0) return ErrorMessage("Bid Price cannot be negative");
    setBidPrice(v);
  };
  const handleBidQuantity = (v) => {
    if (v < 0) return ErrorMessage("Bid Quantity cannot be negative");
    setBidQuantity(v);
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    if (Number(bidQuantity) < Number(requisitionData?.minimumQty))
      return ErrorMessage(
        `Bid Quantity cannot be less than Minimum Quantity (${requisitionData?.minimumQty} MW)`,
      );
    if (Number(bidQuantity) > Number(requisitionData?.requiredQty))
      return ErrorMessage(
        `Bid Quantity cannot exceed Required Quantity (${requisitionData?.requiredQty} MW)`,
      );

    if (showSellerMinQty) {
      if (
        requisitionData?.minimumQty &&
        Number(minimumQuantity) < Number(requisitionData?.minimumQty)
      )
        return ErrorMessage(
          `Seller Min. Quantity cannot be less than Buyer Min. Quantity (${requisitionData?.minimumQty} MW)`,
        );
      if (Number(minimumQuantity) > Number(bidQuantity))
        return ErrorMessage(
          `Seller Min. Quantity cannot exceed Bid Quantity (${bidQuantity} MW)`,
        );
    }

    if (securityMode === "bg" && !bankGuaranteeAmount)
      return ErrorMessage("Please enter the Bank Guarantee amount.");
    if (securityMode === "bg" && !docId)
      return ErrorMessage("Please upload the Bank Guarantee document.");

    try {
      setIsLoading(true);
      const tenderId = tenderData?.data?.id || tenderData?.id;
      const securityPayload =
        securityMode === "bg" ? { bankGuaranteeAmount, documentId: docId } : {};

      const res = await (isUpdate
        ? updateBid({
            id: requisitionData.selfBid.id,
            tenderId,
            bidPrice,
            bidQuantityMw: bidQuantity,
            minBidQuantityMw: showSellerMinQty ? minimumQuantity : 0,
            requisitionId: requisitionData?.id,
            created_by: userId,
            ...securityPayload,
          })
        : createBid({
            tenderId,
            requisitionId: requisitionData?.id,
            initiator: role,
            bidStage,
            bidPrice,
            bidQuantityMw: bidQuantity,
            minBidQuantityMw: showSellerMinQty ? minimumQuantity : 0,
            created_by: userId,
            ...securityPayload,
          }));

      const resData = await decryptData(res?.data);
      if (res?.data?.code === 200 || resData?.code === 200) {
        handleUpdate();
        handleClose();
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setConfirmModal({ open: false, data: "", note: "" });
    }
  };

  // ── Derived UI flags ──────────────────────────────────────────────────────
  const bgLocked = isBgLocked || isBgMandatory;
  const emdLocked = isEmdLocked || isEmdMandatory;

  const bgCaption = isBgLocked
    ? "BG has been submitted and cannot be modified."
    : "BG is mandatory as another requisition has already submitted a BG.";

  const emdCaption = isEmdLocked
    ? "EMD has been selected and cannot be changed."
    : "EMD is mandatory as another requisition has already selected EMD.";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {confirmModal.open && (
        <ConfirmationModal
          open={confirmModal.open}
          handleClose={() =>
            setConfirmModal({ open: false, data: "", note: "" })
          }
          data={confirmModal.data}
          note={confirmModal.note}
          handleSubmit={handleSubmit}
        />
      )}

      {bgDocConfirm.open && (
        <ConfirmationModal
          open={bgDocConfirm.open}
          handleClose={() => {
            setBgDocConfirm({ open: false, pendingVal: null });
            setBgUploadKey((k) => k + 1);
          }}
          header="BG Document Upload"
          data="Please ensure that the correct Bank Guarantee (BG) is uploaded and that the original BG is submitted as required. Bids accompanied by an incorrect or invalid Bank Guarantee will be rejected."
          handleSubmit={() => {
            setDocId(bgDocConfirm.pendingVal?.id);
            setBgDocConfirm({ open: false, pendingVal: null });
          }}
        />
      )}

      {securityModeConfirm.open && (
        <ConfirmationModal
          open={securityModeConfirm.open}
          handleClose={() =>
            setSecurityModeConfirm({ open: false, target: null })
          }
          header={
            securityModeConfirm.target === "emd"
              ? "Switch to EMD"
              : "Switch to Bank Guarantee (BG)"
          }
          data={
            securityModeConfirm.target === "emd"
              ? "Amount will be deducted from the escrow based on the System T&C."
              : "You need to upload the BG document and specify the BG Amount to proceed."
          }
          handleSubmit={confirmSecurityModeSwitch}
        />
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="place-bid-modal"
      >
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          {/* ── Header ── */}
          <div
            className={`display_flex_row modalHeading ${Styles.headingText}`}
          >
            <div className={`headingText ${Styles.finalHead}`}>
              {isUpdate ? "Update" : "Place"} Bid(s)
            </div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          <div className={Styles.containerMainElem}>
            {/* ── Bidding info row ── */}
            <div className={Styles.detailsMainElements}>
              <div className="display_flex_row">
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Bidding Duration</div>
                  <div className={Styles.tenderDateTime}>
                    <div className={Styles.tenderDate}>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidStartDateTime,
                          )?.split("-")?.[0]
                        }
                      </span>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidStartDateTime,
                          )?.split("-")?.[1]
                        }
                      </span>
                    </div>
                    <div>to</div>
                    <div className={Styles.tenderDate}>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidCloseDateTime,
                          )?.split("-")?.[0]
                        }
                      </span>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidCloseDateTime,
                          )?.split("-")?.[1]
                        }
                      </span>
                    </div>
                  </div>
                </div>
                <Countdown status={status} tenderData={tenderData} type="bid" />
              </div>

              {/* ── Requisition summary ── */}
              <div className={`display_flex_row ${Styles.reqMainContainer}`}>
                {[
                  {
                    label: "Start and End Date",
                    value: `${formatDateType(requisitionData?.dateStartAt)} to ${formatDateType(requisitionData?.dateEndAt)}`,
                  },
                  {
                    label: "Start and End Time",
                    value: `${requisitionData?.slotStartAt} - ${requisitionData?.slotEndAt === "23:59" ? "24:00" : requisitionData?.slotEndAt}`,
                  },
                  {
                    label: "Req Qty (MW)",
                    value: `${requisitionData?.requiredQty} (MW)`,
                  },
                  {
                    label: "Min Qty (MW)",
                    value: `${requisitionData?.minimumQty} (MW)`,
                  },
                  {
                    label: "Self Bid Qty (MW)",
                    value: requisitionData?.selfBid?.bidQuantityMw ?? "-",
                    active: true,
                  },
                  {
                    label: "Self Bid Price (₹/kWh)",
                    value: requisitionData?.selfBid?.bidPrice ?? "-",
                    active: true,
                  },
                ].map(({ label, value, active }) => (
                  <div
                    key={label}
                    className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                  >
                    <div>{label}</div>
                    <div className={active ? Styles.active_text : undefined}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Input grid ── */}
            <div className={Styles.sectionsGrid}>
              {/* Left: Bid Details */}
              <div className={Styles.sectionCard}>
                <p className={Styles.sectionLabel}>Bid details</p>
                <div className={Styles.sectionFields}>
                  <TextInput
                    allowDecimal
                    heading="Bid Quantity (MW)*"
                    value={bidQuantity}
                    onChange={handleBidQuantity}
                    example="Eg. 20.32"
                    type="number"
                  />
                  {showSellerMinQty && (
                    <TextInput
                      allowDecimal
                      heading="Seller Min. Quantity (MW)*"
                      value={minimumQuantity}
                      onChange={(v) => setMinimumQuantity(v)}
                      example="Eg. 20.00"
                      type="number"
                      disabled={isUpdate}
                    />
                  )}
                  <TextInput
                    allowDecimal
                    heading="Bid Price (₹/kWh)*"
                    value={bidPrice}
                    onChange={handleBidPrice}
                    example="Eg. 10"
                    type="number"
                  />
                </div>
              </div>

              {/* Right: Security Deposit */}
              <div className={Styles.sectionCard}>
                <div className={Styles.sectionLabelRow}>
                  {/* <p className={Styles.sectionLabel}>Security deposit</p> */}
                  {bgLocked && (
                    <span className={Styles.lockedBadge}>🔒 Locked</span>
                  )}
                  {emdLocked && (
                    <span className={Styles.lockedBadge}>🔒 Locked</span>
                  )}
                </div>

                {/* Toggle — BG at index 0, EMD at index 1 */}
                {/* <div className={Styles.toggleBar}>
                  <button
                    className={`${Styles.toggleBtn} ${securityMode === "bg" ? Styles.toggleBtnActive : ""} ${(!canSwitch && securityMode === "emd") || emdLocked ? Styles.toggleBtnDisabled : ""}`}
                    onClick={() => handleSecurityModeClick("bg")}
                    disabled={
                      (!canSwitch && securityMode === "emd") || emdLocked
                    }
                  >
                    BG
                  </button>
                  <button
                    className={`${Styles.toggleBtn} ${securityMode === "emd" ? Styles.toggleBtnActive : ""} ${(!canSwitch && securityMode === "bg") || bgLocked ? Styles.toggleBtnDisabled : ""}`}
                    onClick={() => handleSecurityModeClick("emd")}
                    disabled={(!canSwitch && securityMode === "bg") || bgLocked}
                  >
                    EMD
                  </button>
                </div> */}

                {/* Status caption */}
                {bgLocked && <p className={Styles.stateCaption}>{bgCaption}</p>}
                {emdLocked && (
                  <p className={Styles.stateCaption}>{emdCaption}</p>
                )}
                {!bgLocked && !emdLocked && securityMode === "emd" && (
                  <p className={Styles.stateCaption}>
                    Amount will be deducted from the escrow based on System T&C.
                  </p>
                )}

                {/* BG fields */}
                {securityMode === "bg" && (
                  <div className={Styles.sectionFields}>
                    <TextInput
                      heading="Bank Guarantee Amount (₹)*"
                      value={bankGuaranteeAmount}
                      onChange={(v) => setBankGuaranteeAmount(v)}
                      example="Eg. 500000"
                      type="number"
                      disabled={isBgLocked}
                    />
                    <Fileupload
                      key={bgUploadKey}
                      accept=".pdf"
                      id="bg-document-upload"
                      value={docId}
                      onChange={(val) => {
                        if (isBgLocked) return;
                        if (val?.id)
                          setBgDocConfirm({ open: true, pendingVal: val });
                        else setDocId(undefined);
                      }}
                      height="35"
                      heading="BG Document"
                      example="Upload BG (.pdf)"
                      fileType="pdf"
                      isInPlaceBid
                      disabled={isBgLocked}
                      viewMode={isBgLocked}
                      previewTitle="Bank Guarantee Document Preview"
                      documentType="BANK_GUARANTEE"
                      parentEntityCompanyId={parentEntityCompanyId}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={`display_flex_row_gap_8 ${Styles.buttonContianer}`}>
            <div className="activeButton_type_3" onClick={handleClose}>
              Cancel
            </div>
            <div
              className="activeButton"
              onClick={() =>
                setConfirmModal({
                  open: true,
                  data: `Do you want to ${isUpdate ? "Update" : "Place"} the Bid?`,
                  note: isUpdate
                    ? `Current Bid: ${bidQuantity}MW/₹${bidPrice}/kWh,\n Previous Bid: ${requisitionData.selfBid?.bidQuantityMw}MW/₹${requisitionData.selfBid?.bidPrice}/kWh`
                    : securityMode !== "bg" &&
                      "EMD Amount will be blocked as per Terms and Conditions, if applicable.",
                })
              }
            >
              {isLoading ? "Loading..." : isUpdate ? "Update" : "Submit"}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RFPPlaceBid;
